import { InputHTMLAttributes, useState } from 'react';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { Input } from 'shared/components/ui/Input';
import { cn } from 'shared/utils/cn';

interface PasswordInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  /** Mostra a barra de força da senha (usar no cadastro, não no login). */
  showStrength?: boolean;
}

const LEVELS = [
  { label: 'Muito fraca', color: 'bg-danger', text: 'text-danger' },
  { label: 'Fraca', color: 'bg-danger', text: 'text-danger' },
  { label: 'Média', color: 'bg-warning', text: 'text-warning' },
  { label: 'Forte', color: 'bg-success', text: 'text-success' },
  { label: 'Muito forte', color: 'bg-success', text: 'text-success' },
];

// Pontuação simples e transparente: tamanho + variedade de caracteres.
export function passwordScore(password: string): number {
  if (!password) return 0;
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  return Math.min(4, score);
}

export function PasswordInput({ label = 'Senha', showStrength = false, value, ...props }: PasswordInputProps) {
  const [visible, setVisible] = useState(false);
  const password = String(value ?? '');
  const score = passwordScore(password);
  const level = LEVELS[score];

  return (
    <div className="flex flex-col gap-1.5">
      <Input
        {...props}
        value={value}
        label={label}
        type={visible ? 'text' : 'password'}
        icon={Lock}
        endAdornment={
          <button
            type="button"
            onClick={() => setVisible((current) => !current)}
            className="rounded-md p-1.5 text-ink-secondary hover:bg-chip hover:text-ink"
            aria-label={visible ? 'Ocultar senha' : 'Mostrar senha'}
            aria-pressed={visible}
          >
            {visible ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
          </button>
        }
      />
      {showStrength && password && (
        <div className="flex items-center gap-2" aria-live="polite">
          <div className="flex flex-1 gap-1">
            {[0, 1, 2, 3].map((index) => (
              <span
                key={index}
                className={cn('h-1 flex-1 rounded-full transition-colors', index < Math.max(1, score) ? level.color : 'bg-chip')}
              />
            ))}
          </div>
          <span className={cn('text-xs font-medium', level.text)}>{level.label}</span>
        </div>
      )}
    </div>
  );
}
